import { ACCEPT_CONFIG } from "../artwork-file-types";
import { isMobileBrowser, isIOS, isAndroid, isTouchDevice } from "../utils/browser";

const DEFAULT_FACING_MODE = "environment";
const VALID_FACING_MODES = new Set(["user", "environment"]);

const SUPPORTED_EXTENSIONS = Object.entries(ACCEPT_CONFIG)
  .flatMap(([_, extensions]) => extensions)
  .join(",");
const SUPPORTED_MIME_TYPES = Object.keys(ACCEPT_CONFIG).join(",");
const ACCEPT_IMAGE = `${SUPPORTED_EXTENSIONS},${SUPPORTED_MIME_TYPES}`;
const ACCEPT_IMAGE_WITH_CAPTURE = `${ACCEPT_IMAGE};capture=camera`;
const IS_MOBILE = isMobileBrowser() || isIOS() || isAndroid() || isTouchDevice();

export default class FileInputHandler {
  #$input;

  #isMultiple;

  #facingMode;

  #boundHandlers = new Map();

  #$button;

  constructor(isMultiple, facingMode) {
    this.#isMultiple = isMultiple;
    this.#facingMode = facingMode;
  }

  // Getters
  get $input() {
    return this.#$input;
  }

  // Setters
  setMultiple(flag) {
    this.#isMultiple = Boolean(flag);
    if (this.#$input) {
      this.#$input.multiple = this.#isMultiple;
    }
  }

  setFacingMode(mode) {
    this.#facingMode = VALID_FACING_MODES.has(mode) ? mode : "";
    this.#syncCaptureAttribute();
  }

  // Actions
  createFileInput(legacyAccept) {
    this.#$input = document.createElement("input");
    this.#$input.type = "file";
    this.#$input.accept = legacyAccept ? ACCEPT_IMAGE_WITH_CAPTURE : ACCEPT_IMAGE;
    this.#$input.multiple = this.#isMultiple;

    this.#$input.classList.add("sr-only");
    document.body.appendChild(this.#$input);

    this.#syncCaptureAttribute();
  }

  attachEventListeners(handlers) {
    this.#$button = handlers.$button;
    const clickHandler = handlers.handleClick;
    this.#boundHandlers.set("button:click:true", clickHandler);
    handlers.$button.addEventListener("click", clickHandler, { capture: true });

    this.#addListener(this.#$input, "change", handlers.handleChange);
    this.#addListener(this.#$input, "blur", handlers.handleBlur);
    this.#addListener(this.#$input, "cancel", handlers.unlock);
    this.#addListener(this.#$input, "focus", handlers.handleFocus);
    this.#addListener(document, "visibilitychange", handlers.handleVisibilityChange);

    this.#addListener(document, "touchstart", () => {
      if (handlers.isLocked) {
        const timer = setTimeout(() => handlers.unlock(), 300);
        handlers.addTimer(timer);
      }
    });
  }

  resetInput() {
    if (this.#$input) {
      this.#$input.value = "";
    }
  }

  getFiles() {
    return [...(this.#$input?.files || [])];
  }

  destroy() {
    this.#boundHandlers.forEach((handler, key) => {
      const [element, event, capture] = key.split(":");
      const options = capture === "true" ? { capture: true } : undefined;
      if (element === "input") {
        this.#$input?.removeEventListener(event, handler, options);
      } else if (element === "document") {
        document.removeEventListener(event, handler, options);
      } else if (element === "button") {
        this.#$button?.removeEventListener(event, handler, options);
      }
    });
    this.#boundHandlers.clear();

    this.#$input?.remove();
    this.#$input = null;
    this.#$button = null;
  }

  // Inits
  #addListener(element, event, handler) {
    const boundHandler = handler;
    const elementType = element === this.#$input ? "input" : "document";
    const key = `${elementType}:${event}:false`;
    this.#boundHandlers.set(key, boundHandler);
    element.addEventListener(event, boundHandler);
  }

  #syncCaptureAttribute() {
    if (!this.#$input) return;

    if (IS_MOBILE) {
      const mode = this.#facingMode || DEFAULT_FACING_MODE;
      this.#$input.setAttribute("capture", mode);
    } else if (this.#facingMode) {
      this.#$input.setAttribute("capture", this.#facingMode);
    } else {
      this.#$input.removeAttribute("capture");
    }
  }
}
